import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { addAuditFrameworks, listAllCatalogFrameworks, unwrapPost } from "../../../../helpers/auditHub_helper";
import { frameworkLabel } from "../utils";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_MUTED, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";

const LABEL_COLOR = "#334155";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    minHeight: 40,
    fontSize: "0.875rem",
    color: LABEL_COLOR,
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: `0 0 0 1px ${AH_TEXT}` },
  },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: LABEL_COLOR },
  "& .MuiInputBase-input::placeholder": { color: "#74788d", opacity: 1 },
};

const optionPaperSx = {
  borderRadius: "8px",
  mt: 0.5,
  fontSize: "0.875rem",
  "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "#fff4eb" },
  "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: "#fff4eb" },
};

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const manualCountText = (count) =>
  Number.isInteger(count) ? `${count} manual control${count === 1 ? "" : "s"}` : "";

const AddFrameworksDialog = ({ auditId, account, onlyAddonId, open, onClose, onAdded }) => {
  const [catalog, setCatalog] = useState({ status: "idle", items: [], error: "" });
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const accountNumber = account?.accountNumber;

  useEffect(() => {
    if (!open || !accountNumber) return undefined;
    let cancelled = false;
    setSelected(null);
    setSaving(false);
    setError("");
    setCatalog({ status: "loading", items: [], error: "" });
    listAllCatalogFrameworks({ accountNumber })
      .then((items) => {
        if (!cancelled) setCatalog({ status: "succeeded", items, error: "" });
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalog({ status: "failed", items: [], error: err?.message || "Unable to load frameworks." });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, accountNumber]);

  const options = useMemo(() => {
    const inScope = new Set(
      (Array.isArray(account?.scopes) ? account.scopes : [])
        .map((scope) => Number(scope.framework?.complianceAddonId))
        .filter(Number.isInteger)
    );
    return catalog.items
      .filter((fw) => Number.isInteger(Number(fw.complianceAddonId)) && !inScope.has(Number(fw.complianceAddonId)))
      .filter((fw) => onlyAddonId == null || Number(fw.complianceAddonId) === Number(onlyAddonId))
      .sort((a, b) => frameworkLabel(a).localeCompare(frameworkLabel(b)));
  }, [catalog.items, account, onlyAddonId]);

  const accountLabel = account?.accountName || account?.accountNumber || "this account";
  const loading = catalog.status === "loading" || catalog.status === "idle";
  const nothingToAdd = catalog.status === "succeeded" && options.length === 0;

  const confirm = async () => {
    if (!auditId || !account?.auditAccountId || !selected || saving) return;
    setSaving(true);
    setError("");
    try {
      const result = unwrapPost(
        await addAuditFrameworks(auditId, account.auditAccountId, [Number(selected.complianceAddonId)])
      );
      onAdded({ account, frameworks: [selected], result });
    } catch (err) {
      setError(err?.message || "Unable to add frameworks.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-frameworks-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography id="add-frameworks-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Add Framework
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: saving ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          Add the framework to{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {accountLabel}
          </Box>
          .
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 0.5 }}>
          {onlyAddonId == null
            ? "An assessment covers one framework. Remove it from every account before choosing a different one."
            : "This assessment already has a framework. The same one is added here."}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: AH_ACCENT }} />
            </Box>
          ) : catalog.status === "failed" ? (
            <Typography role="alert" sx={{ fontSize: "0.8125rem", color: "#dc2626" }}>
              {catalog.error}
            </Typography>
          ) : nothingToAdd ? (
            <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED }}>
              {onlyAddonId == null
                ? "Every framework this account can be audited on is already in scope."
                : "This account is not licensed for the assessment's framework."}
            </Typography>
          ) : (
            <>
              <Box
                component="label"
                htmlFor="add-frameworks-picker"
                sx={{ display: "block", mb: "6px", fontSize: "0.8125rem", fontWeight: 600, color: LABEL_COLOR }}
              >
                Framework
                <Box component="span" sx={{ color: "#dc2626", ml: 0.25 }} aria-hidden="true">
                  *
                </Box>
              </Box>
              <Autocomplete
                size="small"
                id="add-frameworks-picker"
                disabled={saving}
                options={options}
                value={selected}
                onChange={(_event, value) => {
                  setSelected(value);
                  if (error) setError("");
                }}
                getOptionLabel={(fw) => frameworkLabel(fw)}
                isOptionEqualToValue={(a, b) => Number(a.complianceAddonId) === Number(b.complianceAddonId)}
                slotProps={{ paper: { sx: optionPaperSx } }}
                renderOption={(props, fw) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box component="li" key={key} {...optionProps} sx={{ display: "block !important" }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: AH_TEXT }}>
                        {frameworkLabel(fw)}
                      </Typography>
                      {manualCountText(fw.manualControlCount) ? (
                        <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED }}>
                          {manualCountText(fw.manualControlCount)}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Framework" sx={fieldSx} />
                )}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Cancel
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={saving || !selected}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...dialogButtonSx,
            backgroundColor: AH_ACCENT,
            color: AH_WHITE,
            "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: AH_ACCENT, color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {saving ? "Adding" : "Add"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddFrameworksDialog;
